import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useProgress } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';

// WarmShaders — compile every material in the scene BEFORE the fly-in.
//
// three compiles a material's shader the first time something using it is
// DRAWN. Behind the landing page the camera hangs over the city, so anything
// small, far or frustum-culled from up there (the residents by the spawn, the
// street furniture, the face decals) first gets drawn during the cinematic
// descent, and each first draw is a synchronous compile + link on the main
// thread: 100–250ms stalls in the one shot that must stay smooth. Measured
// (2026-09-23): ~900ms of long tasks during the fly-in before, none after.
//
// compileAsync walks the whole scene regardless of what the camera can see and
// uses KHR_parallel_shader_compile where the driver has it, so the programs
// build in the background while the landing page is up. Runs once the loading
// manager has been quiet for a moment (the same "settled" idea the Loader's
// gate uses), again whenever a new batch of loads settles before entry, and
// never after the visitor is in — from then on the streaming queue owns
// pacing, and a full-scene compile mid-walk would be its own hitch.
//
// POLLED, NOT SUBSCRIBED. The loading store is written from inside other
// components' renders (useGLTF suspending), and a hook subscription here made
// React warn about updating one component while rendering another.
const QUIET_MS = 600;
const POLL_MS = 200;

export default function WarmShaders() {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);

  useEffect(() => {
    if (!gl.compileAsync) return undefined;
    let lastLoaded = -1; let quietSince = 0; let busy = false;
    const id = setInterval(() => {
      if (useWorldStore.getState().entered) { clearInterval(id); return; }
      const { active, loaded } = useProgress.getState();
      const now = performance.now();
      if (active || busy || loaded === lastLoaded) { quietSince = now; return; }
      if (now - quietSince < QUIET_MS) return;
      busy = true;
      lastLoaded = loaded;
      const t0 = performance.now();
      gl.compileAsync(scene, camera)
        .then(() => {
          const ms = Math.round(performance.now() - t0);
          if (ms > 5) console.log(`[shaders] warmed before entry in ${ms}ms (${gl.info.programs?.length ?? '?'} programs)`);
        })
        .catch(() => { /* a failed warm-up only means the first draw compiles, as before */ })
        .finally(() => { busy = false; });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [gl, scene, camera]);

  return null;
}
